import { CHROME_FONT_SIZE, IE11_FONT_SIZE } from '@ispm/shared/bpmn-io/bpmn-viewer-styling';
import { ExecutionPath, TreeNavigationConfiguration } from '@ispm-client/modules/process-executor/models';
import { IEService } from '@ispm/shared/services/IE.service';
import { ExecutedPathUtils } from '@ispm-client/modules/process-executor/services/executed-path-utils';
import { ModelingUtil } from '@ispm/shared/bpmn-io/modeling-util';
import { HEAD_NODE_ID } from '@ispm-shared/modules/bpmn/diagram-element-terms';
import { Shape } from 'diagram-js/lib/model';
import { isEmpty } from 'lodash';
import { Canvas } from 'diagram-js/lib/core/Canvas';
import { ElementRegistry } from 'diagram-js/lib/core/ElementRegistry';
import { Range, DimensionsCalculator, ROI } from '@ispm/shared/bpmn-io/services/dimensions-calculator';

export class TreePathNavigator {

  private DEFAULT_FONT_SIZE = CHROME_FONT_SIZE;
  private navigationConfiguration: TreeNavigationConfiguration;
  private firstLoad: boolean;

  constructor(private ieService: IEService,
              private canvas,
              private elementRegistry,
              private dimensionsCalculator: DimensionsCalculator) {
    this.navigationConfiguration = new TreeNavigationConfiguration();
    if (this.ieService.isIE()) {
      this.DEFAULT_FONT_SIZE = IE11_FONT_SIZE;
    }
  }

  /**
   * @param executionPath - the recenter is based on the nodes at executionPath
   * @param firstLoad - in case this is not first load of the tree, thus user see the screen,
   * the system shall not move the tree in case current and nodes for selection are visible
   *
   * @algorithm:
   * 1. Find required nodes which can fit in current viewbox size
   * 2. Compute the box, defined by the most outer coordinates of those nodes
   * 3. Align that box to left center = newViewBox
   * 4. If there is empty place on the right and nodes on the left of the newViewBox, move newViewBox to left to fill that space
   * 5. If all nodes at axis y visible at newViewBox centralize on y by all nodes height
   * 6. If there is empty place above visible nodes in the newViewBox or
   * at the bottom newViewBox on y to the outer y of all nodes accordingly
   */
  recenter(executionPath: ExecutionPath, firstLoad: boolean) {
    if (!firstLoad && this.canFitInCurrentViewBox(executionPath)) {
      return;
    }
    const roi: ROI = this.calculateROIOfLastExecuted(executionPath);
    this.canvas.viewbox({
      x: roi.minX,
      y: roi.minY,
      width: roi.width,
      height: roi.height
    });
  }

  /**
   * compute the box, defined by the most outer coordinates of all given nodes.
   */
  private computeFittingBoxByIds(nodesToDisplay: Array<string>): ROI {
    const shapes = nodesToDisplay.map(nodeId => this.elementRegistry.get(nodeId));
    return this.dimensionsCalculator.computeFittingBoxOfNodes(shapes);
  }

  private calculateROIOfLastExecuted(executionPath: ExecutionPath): ROI {
    let executedNodesToDisplay: Array<string> = [];
    let nodesForSelectionIds: Array<string> = new Array<string>();
    if (executionPath.executedTreatmentTaskId) {
      executedNodesToDisplay = ExecutedPathUtils.getLastExecutedNodesInCurrentLayer(Number.MAX_SAFE_INTEGER, executionPath.executedNodes);
      executedNodesToDisplay.push(executionPath.executedTreatmentTaskId);
    } else {
      const n = this.navigationConfiguration.numberOfLastExecutedNodesToShow;
      executedNodesToDisplay = ExecutedPathUtils.getLastNExecutedNodes(executionPath, n);
      if (executionPath.nodesForSelection) {
        nodesForSelectionIds = executionPath.nodesForSelection.map(node => node.nodeId);
      }
    }
    return this.calculateROI(executedNodesToDisplay, nodesForSelectionIds, this.getAllNodesAtCurrentLayer(executionPath));
  }

  private calculateROI(executedNodesToDisplay: Array<string>,
                       nodesForSelectionIds: Array<string>,
                       allShapesAtCurrentLayer) {
    const fittingBoxOfRequiredNodes = this.calcROIOfRequiredViewableNodes(executedNodesToDisplay, nodesForSelectionIds);
    const minX = this.placeOnX(fittingBoxOfRequiredNodes, this.canvas.viewbox().width, allShapesAtCurrentLayer);
    const minY = this.placeOnY(
      new Range(fittingBoxOfRequiredNodes.minY, fittingBoxOfRequiredNodes.height),
      new Range(minX, this.canvas.viewbox().width),
      this.canvas.viewbox().height,
      allShapesAtCurrentLayer);
    return {
      minX: minX,
      width: this.canvas.viewbox().width,
      minY: minY,
      height: this.canvas.viewbox().height
    };
  }

  private calcROIOfRequiredViewableNodes(executedNodesToDisplay: Array<string>, nodesForSelectionIds: Array<string>): ROI {
    const current = executedNodesToDisplay[executedNodesToDisplay.length - 1];
    while (executedNodesToDisplay.length >= 1) {
      const roi = this.computeFittingBoxByIds([...executedNodesToDisplay, ...nodesForSelectionIds]);
      if (this.dimensionsCalculator.willItFit(roi, this.canvas.viewbox())) {
        return roi;
      }
      executedNodesToDisplay = executedNodesToDisplay.slice(1, executedNodesToDisplay.length);
    }
    return this.computeFittingBoxByIds([current]);
  }

  private getAllNodesAtCurrentLayer(executionPath: ExecutionPath) {
    const currentLayerHead = ExecutedPathUtils.getFirstExecutedNodeAtCurrentLayer(executionPath);
    return ModelingUtil.getDescendentNodesAtCurrentLayer(this.elementRegistry.get(currentLayerHead));
  }

  private calculateCandidateMinY(candidate: Range, allNodes: Range, currentViewBoxHeight: number): number {
    if (allNodes.size <= currentViewBoxHeight) {
      return this.dimensionsCalculator.centralizeOnAxis(allNodes, currentViewBoxHeight);
    }

    const candidateCentralizedY: Range = new Range(
      this.dimensionsCalculator.centralizeOnAxis(candidate, currentViewBoxHeight),
       currentViewBoxHeight
    );

    if (candidateCentralizedY.start <= allNodes.start) {
      return allNodes.start;
    }

    return this.shiftToTheAxisEndIfRequired(candidateCentralizedY, allNodes);
  }

  private calculateCandidateMinX(candidate: Range, allNodes: Range): number {
    if ( allNodes.size <= candidate.size) {
      return allNodes.start;
    }
    return this.shiftToTheAxisEndIfRequired(candidate, allNodes);
  }

  private shiftToTheAxisEndIfRequired(candidate: Range, allNodes: Range) {
    let result = candidate.start;
    if (allNodes.end < candidate.end) {
      result = allNodes.end - candidate.size;
    }
    return result;
  }

  /**
   * @param executionPath
   * @algorithm:
   * In case path ends at main tree use same algorithm as in #recenter
   * In case path continues into neighborhood try to show as much as possible of the path at main tree,
   * when executed neighborhood entry must be shown
   * To calculate this is used same algorithm as in recenter applied on all nodes at executedPath at main tree
   */
  recenterMainTreeExitingNeighborhood(executionPath: ExecutionPath) {
    this.firstLoad = true;
    if (ExecutedPathUtils.doesExecutedPathEndAtMainTree(executionPath)) {
      this.recenter(executionPath, true);
    } else {
      const pathAtMainTree: Array<string>  = ExecutedPathUtils.getPathAtMainTree(executionPath);
      const shapesAtCurrentLevel = ModelingUtil.getDescendentNodesAtCurrentLayer(this.elementRegistry.get(HEAD_NODE_ID));
      const roi: ROI = this.calculateROI(pathAtMainTree, [], shapesAtCurrentLevel);

      this.canvas.viewbox({
        x: roi.minX,
        y: roi.minY,
        width: roi.width,
        height: roi.height
      });
    }
  }

  private getVisibleNodesAtRange(roi: ROI, shapesAtCurrentLevel) {
    return shapesAtCurrentLevel.filter(shape => this.doRangesIntersect(roi, this.shapeToROI(shape)));
  }

  private doCoordinatesIntersect(coord1: Range, coord2: Range) {
    return (coord2.start >= coord1.start && coord2.start <= coord1.start + coord1.size) ||
      (coord2.start + coord2.size >= coord1.start && coord2.start + coord2.size <= coord1.start + coord1.size);
  }

  private doRangesIntersect(roi1: ROI, roi2: ROI) {
    return this.doCoordinatesIntersect(new Range(roi1.minX, roi1.width), new Range(roi2.minX, roi2.width)) &&
      this.doCoordinatesIntersect(new Range(roi1.minY, roi1.height), new Range(roi2.minY, roi2.height));
  }

  private shapeToROI(shape: Shape): ROI {
    return { minX: shape.x, width: shape.width, minY: shape.y, height: shape.height};
  }

  private canFitInCurrentViewBox(executionPath: ExecutionPath) {
    let roi: ROI;
    if (!isEmpty(executionPath.nodesForSelection)) {
      roi = this.computeFittingBoxByIds(
        [executionPath.currentUserTaskHolder.nodeId, ...executionPath.nodesForSelection.map(node => node.nodeId)]);
    } else {
      roi = this.computeFittingBoxByIds(
        [executionPath.executedNodes[executionPath.executedNodes.length - 1].nodeId, executionPath.executedTreatmentTaskId]);
    }
    return this.dimensionsCalculator.doesViewBoxContainROI(roi, this.canvas.viewbox());
  }

  private placeOnX(fittingBoxOfRequiredNodes: ROI, viewBoxWidth: number, allShapesAtCurrentLevel): number {
    const maxRange = this.calcMaxRange(new Range(fittingBoxOfRequiredNodes.minX, fittingBoxOfRequiredNodes.width), viewBoxWidth);
    const viewSizeWrapper: ROI = {
      minX: maxRange.start,
      width: maxRange.size,
      minY: this.dimensionsCalculator.centralizeOnAxis(new Range(fittingBoxOfRequiredNodes.minY, fittingBoxOfRequiredNodes.height), this.canvas.viewbox().height),
      height: this.canvas.viewbox().height
    };
    const fittingBoxOfNodesAtMaxRange = this.dimensionsCalculator.computeFittingBoxOfNodes(this.getVisibleNodesAtRange(viewSizeWrapper, allShapesAtCurrentLevel));
    return this.calculateCandidateMinX(new Range(fittingBoxOfRequiredNodes.minX, viewBoxWidth),
      new Range(fittingBoxOfNodesAtMaxRange.minX, fittingBoxOfNodesAtMaxRange.width));
  }

  private placeOnY(yCenterRange: Range, xRange: Range, viewBoxHeight, allShapesAtCurrentLayer): number {
    const maxRange = this.calcMaxRange(yCenterRange, viewBoxHeight);
    const viewSizeWrapper = {
      minX: xRange.start,
      width: xRange.size,
      minY: maxRange.start,
      height: maxRange.size
    };
    const fittingBoxOfNodesAtMaxRange =
      this.dimensionsCalculator.computeFittingBoxOfNodes(this.getVisibleNodesAtRange(viewSizeWrapper, allShapesAtCurrentLayer));
    return this.calculateCandidateMinY(
      yCenterRange,
      new Range(fittingBoxOfNodesAtMaxRange.minY, fittingBoxOfNodesAtMaxRange.height),
      viewBoxHeight);
  }

  private calcMaxRange(originalRange: Range, viewBoxSize: number): Range {
    const start = originalRange.end - viewBoxSize;
    const end = originalRange.start + viewBoxSize;
    return new Range(start, end - start);
  }

}
